import { useState } from 'react'
import { confirmationInvitation, initiales, inviterMembre, modifierMembre, suspendreMembre } from '../../api/operations'
import { CAPACITES, DESCRIPTION_ROLE, LIBELLE_ROLE, ROLES } from '../../api/roles'
import type { DonneesOps, Membre, RoleMembre } from '../../api/types'
import { useDroits } from '../Contexte'
import { Icone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'
import { ChampTelephone, contactAffiche, useTelephone, validerContact, type ContactValide } from '../../api/commun'
import { supabase } from '../../api/supabase'

/**
 * Équipe & rôles — transposition de `renderEquipe` / `openMemberModal` /
 * `saveMember` / `revokeMember` / `reactivateMember`.
 *
 * ⚠️ ÉCARTS ASSUMÉS avec l'original, parce qu'il MENTAIT :
 *  • « Rôles personnalisés » (nouveau rôle, nom, description, cases) : un
 *    tableau en mémoire, perdu au rechargement. `public.member_role` est une
 *    énumération figée à quatre valeurs (0001) et aucune table `roles`
 *    n'existe. Non reproduit.
 *  • Cases de droits par membre : écrites dans `memberships.permissions`,
 *    colonne qu'AUCUNE policy RLS ni AUCUNE edge function ne lit. Cocher
 *    « Voir la comptabilité : non » ne retirait rien. Non reproduites.
 *
 * Seul `memberships.role` décide ; la matrice ci-dessous dit ce qu'il permet,
 * d'après les policies et les edge functions (voir `roles.ts`).
 */

const LIBELLE_STATUT: Record<string, [string, string]> = {
  active: ['Actif', 'status-active'],
  invited: ['Invité', 'status-invited'],
  suspended: ['Suspendu', 'status-suspended'],
}

/** Rôles qu'on peut donner depuis la console : un livreur se crée depuis « Livreurs ». */
const ROLES_EQUIPE: RoleMembre[] = ['owner', 'dispatcher', 'accountant']

export function EcranEquipe({
  donnees,
  orgId,
  surRafraichir,
  surNotifier,
}: {
  donnees: DonneesOps
  orgId: string
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
}) {
  const [edition, setEdition] = useState<Membre | 'nouveau' | null>(null)
  const droits = useDroits()
  const gere = droits.peut('equipe')

  const actifs = donnees.members.filter((m) => m.status !== 'suspended').length

  async function suspendre(m: Membre) {
    if (!window.confirm(`Suspendre l'accès de ${m.full_name} ?`)) return
    try {
      await suspendreMembre(orgId, m.id)
      surNotifier('Accès suspendu')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  async function reactiver(m: Membre) {
    try {
      await modifierMembre(orgId, m.id, { status: 'active' })
      surNotifier('Accès réactivé')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  return (
    <>
      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <span className="h-ic">
                <Icone nom="shield" />
              </span>{' '}
              Équipe
            </h3>
            <div className="ch-sub">
              {actifs} membre{actifs > 1 ? 's' : ''} actif{actifs > 1 ? 's' : ''} · créés par invitation
            </div>
          </div>
          <span className="sp" />
          {gere ? (
            <button className="btn btn-gold btn-sm" onClick={() => setEdition('nouveau')}>
              <Icone nom="plus" /> Inviter un membre
            </button>
          ) : (
            <span className="t-muted">{droits.refus} Seul le propriétaire gère l’équipe.</span>
          )}
        </div>
        <div className="card-body flush">
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Membre</th>
                  <th>Contact</th>
                  <th>Rôle</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {donnees.members.map((m) => {
                  const [libelleStatut, classeStatut] = LIBELLE_STATUT[m.status] ?? ['—', 'status-suspended']
                  return (
                    <tr key={m.id}>
                      <td>
                        <div className="u-cell">
                          <span className="avatar">{initiales(m.full_name)}</span>
                          <div>
                            <div className="t-strong">{m.full_name}</div>
                            {m.title && <div className="t-muted">{m.title}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="t-muted">{contactAffiche(m.email, m.phone) || '—'}</td>
                      <td>
                        <span className={`role-chip role-${m.role}`}>{LIBELLE_ROLE[m.role] ?? m.role}</span>
                      </td>
                      <td>
                        <span className={`status-dot ${classeStatut}`}>{libelleStatut}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {gere && m.role !== 'driver' && (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => setEdition(m)}>
                              <Icone nom="edit" /> Modifier
                            </button>
                            {m.role !== 'owner' &&
                              (m.status === 'suspended' ? (
                                <button className="btn btn-ghost btn-sm" onClick={() => void reactiver(m)}>
                                  Réactiver
                                </button>
                              ) : (
                                <button
                                  className="btn btn-outline btn-sm"
                                  style={{ color: 'var(--err)' }}
                                  onClick={() => void suspendre(m)}
                                  aria-label={`Suspendre ${m.full_name}`}
                                  title="Suspendre l'accès"
                                >
                                  <Icone nom="x" />
                                </button>
                              ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <span className="h-ic">
                <Icone nom="gear" />
              </span>{' '}
              Ce que chaque rôle peut faire
            </h3>
            <div className="ch-sub">
              Quatre rôles, fixés par la base. Le rôle d’un membre est la SEULE chose qui décide de ses droits.
            </div>
          </div>
        </div>
        <div className="card-body flush">
          <div className="tbl-wrap">
            <table className="matrice-roles">
              <thead>
                <tr>
                  <th>Capacité</th>
                  {ROLES.map((r) => (
                    <th key={r} style={{ textAlign: 'center' }}>
                      {LIBELLE_ROLE[r]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CAPACITES.map((c) => (
                  <tr key={c.cle}>
                    <td style={{ whiteSpace: 'normal' }}>
                      <div className="t-strong">{c.libelle}</div>
                      <div className="t-muted" style={{ fontSize: 11 }}>
                        {c.source}
                      </div>
                    </td>
                    {ROLES.map((r) => (
                      <td key={r} style={{ textAlign: 'center' }}>
                        {c.roles.includes(r) ? (
                          <span className="oui">
                            <span className="visuellement-masque">Oui</span>
                          </span>
                        ) : (
                          <span className="non">
                            <span className="visuellement-masque">Non</span>
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-body">
          {ROLES.map((r) => (
            <p key={r} style={{ fontSize: 12.5, color: 'var(--txt2)', margin: '0 0 6px' }}>
              <span className={`role-chip role-${r}`}>{LIBELLE_ROLE[r]}</span> {DESCRIPTION_ROLE[r]}
            </p>
          ))}
          <div className="bandeau" style={{ marginTop: 10 }}>
            Les « rôles personnalisés » et les cases de droits de l’ancienne console n’ont pas été repris : rien en
            base ne les appliquait. Pour restreindre quelqu’un, choisissez le rôle qui correspond à son travail.
          </div>
        </div>
      </div>

      {edition && gere && (
        <ModaleMembre
          membre={edition === 'nouveau' ? null : edition}
          surFermer={() => setEdition(null)}
          surEnregistrer={async (v) => {
            if (edition === 'nouveau') {
              if (!v.contact) return
              const r = await inviterMembre(orgId, { contact: v.contact, role: v.role, title: v.titre })
              surNotifier(confirmationInvitation(r))
            } else {
              await modifierMembre(orgId, edition.id, { role: v.role, title: v.titre })
              surNotifier('Membre mis à jour')
            }
            setEdition(null)
            await surRafraichir()
          }}
        />
      )}
    </>
  )
}

function ModaleMembre({
  membre,
  surFermer,
  surEnregistrer,
}: {
  membre: Membre | null
  surFermer: () => void
  surEnregistrer: (v: { contact: ContactValide | null; role: RoleMembre; titre: string | null }) => Promise<void>
}) {
  const [email, setEmail] = useState(membre?.email ?? '')
  const { valeur: telephone, setValeur: setTelephone } = useTelephone(supabase)
  const [titre, setTitre] = useState(membre?.title ?? '')
  const [role, setRole] = useState<RoleMembre>(membre?.role ?? 'dispatcher')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  async function enregistrer() {
    /* Invitation : e-mail OU numéro WhatsApp, au moins un. La modification d'un membre ne touche pas à ses coordonnées. */
    let contact: ContactValide | null = null
    if (!membre) {
      const c = validerContact({ email, telephone })
      if (!c.ok) {
        setErreur(c.message)
        return
      }
      contact = c
    }
    setEnCours(true)
    setErreur(null)
    try {
      await surEnregistrer({ contact, role, titre: titre.trim() || null })
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale
      titre={membre ? `Modifier ${membre.full_name}` : 'Inviter un membre'}
      ouverte
      surFermer={surFermer}
      surEnregistrer={() => void enregistrer()}
      libelleEnregistrer={membre ? 'Enregistrer' : "Envoyer l'invitation"}
      enCours={enCours}
    >
      {membre ? (
        <div className="field">
          <label htmlFor="mb-email">Contact</label>
          <input id="mb-email" value={contactAffiche(membre.email, membre.phone)} disabled readOnly />
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="mb-email">E-mail (recommandé){" · l'invitation part ici"}</label>
            <input
              id="mb-email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErreur(null) }}
              placeholder="prenom@yiriwa-group.com"
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <ChampTelephone
              valeur={telephone}
              surChangement={(v) => { setTelephone(v); setErreur(null) }}
              etiquette="Numéro WhatsApp"
              montrerErreur
              aide="Au moins un des deux. Numéro seul : aucun e-mail n'est envoyé, la personne se connecte avec un code reçu sur WhatsApp."
            />
          </div>
        </>
      )}
      <div className="field">
        <label htmlFor="mb-titre">Intitulé de poste (affichage seulement)</label>
        <input id="mb-titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="ex. Dispatch (nuit)" />
      </div>
      <div className="field">
        <label>Rôle</label>
        <ListeDeroulante
          valeur={role}
          options={ROLES_EQUIPE.map((r) => ({ valeur: r, libelle: LIBELLE_ROLE[r] }))}
          surChangement={(v) => ROLES_EQUIPE.includes(v as RoleMembre) && setRole(v as RoleMembre)}
          pleineLargeur
        />
        <p className="t-muted" style={{ marginTop: 8, fontSize: 12 }}>
          {DESCRIPTION_ROLE[role]}
        </p>
      </div>
      {erreur && <p style={{ color: 'var(--err)', fontSize: 12.5, marginTop: 8 }}>{erreur}</p>}
    </Modale>
  )
}
